import { ChevronDown } from 'lucide-react';
import React, { useEffect, useState } from 'react';

import { Node } from '../types/narrative';
import { getWorldFromNodeId } from '../utils/narrativeHelpers';

interface VariantContentRendererProps {
  node: Node;
  field: 'text';
}

export const VariantContentRenderer = function VariantContentRenderer({
  node,
  field,
}: VariantContentRendererProps) {
  const [selectedVariant, setSelectedVariant] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const buttonRef = React.useRef<HTMLButtonElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (buttonRef.current && !buttonRef.current.contains(event.target as HTMLElement)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showDropdown]);

  const worldFromNodeId = getWorldFromNodeId(node.id);

  // Only apply special rendering for conditional nodes and 'text' field
  if (worldFromNodeId && node.type === 'Conditional' && field === 'text') {
    const worldKey = worldFromNodeId === 'good' ? 'goodWorld' : 'badWorld';
    const currentWorldVariants = node.variantContent?.[worldKey]?.variants || [];
    const hasVariantsDefined = currentWorldVariants.length > 0;

    const nodeText = node.text || '';
    const hasPlaceholder = nodeText.includes('[VARIANT]');

    const textAfterFromField =
      worldFromNodeId === 'good' ? node.textAfterGoodWorld : node.textAfterBadWorld;

    let textBefore = '';
    let textAfterIfNoField = '';

    if (hasPlaceholder) {
      const parts = nodeText.split('[VARIANT]');
      textBefore = parts[0] || '';
      textAfterIfNoField = parts.slice(1).join('[VARIANT]');
    } else {
      textBefore = nodeText;
    }

    const textAfter = textAfterFromField || textAfterIfNoField;

    if (hasVariantsDefined) {
      const currentVariant = currentWorldVariants[selectedVariant];
      const displayVariantLine = currentVariant?.variantLine || '(empty variant)';
      return (
        <div className="relative">
          <span>{textBefore}</span>
          <span className="relative inline-block">
            <button
              ref={buttonRef}
              onClick={(e) => {
                e.stopPropagation();
                if (!showDropdown && buttonRef.current) {
                  const rect = buttonRef.current.getBoundingClientRect();
                  setDropdownPosition({
                    top: rect.bottom + window.scrollY,
                    left: rect.left + window.scrollX,
                  });
                }
                setShowDropdown(!showDropdown);
              }}
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-sm mx-1 transition-colors cursor-pointer ${
                worldFromNodeId === 'good'
                  ? 'bg-green-600 hover:bg-green-700 text-white'
                  : 'bg-red-600 hover:bg-red-700 text-white'
              }`}
            >
              <span>{displayVariantLine}</span>
              <ChevronDown className="w-3 h-3" />
            </button>

            {showDropdown && (
              <div
                className="bg-[#1a223a] border border-[#2a3756] rounded-md shadow-lg min-w-[200px]"
                style={{
                  position: 'fixed',
                  top: `${dropdownPosition.top}px`,
                  left: `${dropdownPosition.left}px`,
                  zIndex: 9999,
                  maxHeight: '300px',
                  overflowY: 'auto',
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-2">
                  <p className="text-xs text-gray-400 mb-2 px-1">
                    Preview for {worldFromNodeId === 'good' ? 'Better World' : 'Worse World'}:
                  </p>
                  {currentWorldVariants.map((variant, idx) => (
                    <button
                      key={idx}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedVariant(idx);
                        setShowDropdown(false);
                      }}
                      className={`
                          w-full text-left px-3 py-2 rounded text-sm hover:bg-[#2a3756] transition-colors
                          ${selectedVariant === idx ? 'bg-[#2a3756] text-white' : 'text-gray-300'}
                        `}
                    >
                      <div className="font-medium mb-1">{`Variant for: "${variant.choiceText}"`}</div>
                      <div
                        className={`text-xs mt-1 ${
                          worldFromNodeId === 'good' ? 'text-green-400' : 'text-red-400'
                        }`}
                      >
                        → {variant.variantLine || '(no variant text)'}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </span>
          <span>{textAfter}</span>
        </div>
      );
    } else {
      // If no variants are defined for this world, but the node's text has the [VARIANT] placeholder,
      // indicate that variants need to be defined.
      return (
        <div className="relative">
          <span>{textBefore}</span>
          {hasPlaceholder && (
            <span
              className={`italic mx-1 ${worldFromNodeId === 'good' ? 'text-green-400/70' : 'text-red-400/70'}`}
            >
              [Define Variants]
            </span>
          )}
          <span>{textAfter}</span>
        </div>
      );
    }
  }

  // Standard rendering for other nodes or fields
  const defaultText = node[field] || '';
  return <>{defaultText || 'Your text will appear here...'}</>;
};
